import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { BarChart } from '../../components/charts/BarChart';
import { chartColour, topN } from '../../components/charts/palette';
import { Button } from '../../components/Button';
import { EmptyState } from '../../components/EmptyState';
import { ErrorNotice } from '../../components/ErrorNotice';
import { PageHeader } from '../../components/PageHeader';
import { SkeletonTable } from '../../components/Skeleton';
import { downloadTimeReport, REPORT_AXES, type ReportAxis, type TimeFilters } from './api';
import { formatDuration, formatHours } from './duration';
import { useTimeReport } from './queries';
import { useFileDownload } from '../tickets/articles/attachmentFile';
import { Select } from '../../components/Select';

/**
 * Die Zeitauswertung -- F5-04, K-129.
 *
 * **Die Achse ist eine Auswahl und kein Feldname.** Der Server nimmt nur die
 * fuenf zugelassenen entgegen (docs/06, F5-07: kein freies SQL aus der
 * Oberflaeche); die Liste hier ist deckungsgleich mit `GROUP_BY_AXES` im
 * Dienst.
 *
 * **Minuten und Stunden stehen nebeneinander.** Minuten sind die gespeicherte
 * Groesse und rechnen sich verlustfrei; Stunden sind das, worueber gesprochen
 * wird. Wer nur eines zeigt, zwingt den Leser zur Umrechnung -- und einer
 * rundet anders.
 *
 * **Das Bild steht ueber der Tabelle, nicht statt ihrer.** Die Frage "wer
 * traegt die Last" beantwortet ein Balken in einem Blick; die Frage "wie
 * viele Stunden genau" beantwortet nur die Zeile darunter. Das Diagramm
 * zeigt die groessten zehn Gruppen und sagt dabei, dass es gekuerzt hat.
 */
export function TimeReportPage() {
  const { t } = useTranslation();

  const [groupBy, setGroupBy] = useState<ReportAxis>('customer');
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [onlyBillable, setOnlyBillable] = useState(false);

  const filters: TimeFilters = {
    ...(since ? { since } : {}),
    ...(until ? { until } : {}),
    ...(onlyBillable ? { billable: true } : {}),
  };

  const report = useTimeReport(filters, groupBy);
  const exportFile = useFileDownload();

  return (
    <>
      <PageHeader
        title={t('time.report')}
        subtitle={t('time.reportSubtitle')}
        action={
          <Button
            variant="secondary"
            busy={exportFile.isPending}
            onClick={() => {
              exportFile.mutate({
                fetchFile: () => downloadTimeReport(filters, groupBy),
                fallbackName: `zeiten-${groupBy}.csv`,
              });
            }}
          >
            {t('time.downloadCsv')}
          </Button>
        }
      />

      <section className="card">
        <div className="row">
          <div className="field">
            <label className="field__label" htmlFor="report-group-by">
              {t('time.groupBy')}
            </label>
            <Select
              id="report-group-by"
              className="field__control"
              value={groupBy}
              onChange={(event) => {
                setGroupBy(event.target.value as ReportAxis);
              }}
            >
              {REPORT_AXES.map((axis) => (
                <option key={axis} value={axis}>
                  {t(`reportAxis.${axis}`)}
                </option>
              ))}
            </Select>
          </div>

          <div className="field">
            <label className="field__label" htmlFor="report-since">
              {t('time.from')}
            </label>
            <input
              id="report-since"
              type="date"
              className="field__control"
              value={since}
              onChange={(event) => {
                setSince(event.target.value);
              }}
            />
          </div>

          <div className="field">
            <label className="field__label" htmlFor="report-until">
              {t('time.to')}
            </label>
            <input
              id="report-until"
              type="date"
              className="field__control"
              value={until}
              onChange={(event) => {
                setUntil(event.target.value);
              }}
            />
          </div>
        </div>

        <label className="field__checkbox">
          <input
            type="checkbox"
            checked={onlyBillable}
            onChange={(event) => {
              setOnlyBillable(event.target.checked);
            }}
          />
          {t('time.onlyBillable')}
        </label>
      </section>

      {report.data && report.data.buckets.length > 0 ? (
        <section className="card">
          <div className="chart-card__head">
            <h2>{t(`reportAxis.${groupBy}`)}</h2>
            <p className="chart-card__hint">{t('time.chartHint')}</p>
          </div>
          <BarChart
            caption={t('time.report')}
            data={topN(
              report.data.buckets.map((bucket) => ({
                key: bucket.key,
                label: bucket.label,
                value: bucket.minutes,
              })),
              10,
              (value, count) => ({
                key: '__rest__',
                label: t('charts.other', { count }),
                value,
              }),
            ).map((row, index) => ({
              ...row,
              display: formatHours(row.value),
              colour: row.key === '__rest__' ? 'var(--chart-8)' : chartColour(index),
            }))}
          />
        </section>
      ) : null}

      <section className="card card--flush">
        {report.isPending ? <SkeletonTable columns={5} /> : null}
        {report.isError ? <ErrorNotice error={report.error} /> : null}
        {exportFile.isError ? <ErrorNotice error={exportFile.error} /> : null}

        {report.data?.buckets.length === 0 ? (
          <EmptyState icon="time" title={t('time.noData')} hint={t('time.noDataHint')} />
        ) : null}

        {report.data && report.data.buckets.length > 0 ? (
          <div className="table-wrapper table-wrapper--tall">
            <table className="table">
              <thead>
                <tr>
                  <th scope="col">{t(`reportAxis.${groupBy}`)}</th>
                  <th scope="col">{t('time.minutes')}</th>
                  <th scope="col">{t('time.hours')}</th>
                  <th scope="col">{t('time.billableMinutes')}</th>
                  <th scope="col">{t('time.entries')}</th>
                </tr>
              </thead>
              <tbody>
                {report.data.buckets.map((bucket) => (
                  <tr key={bucket.key}>
                    <td>{bucket.label}</td>
                    <td className="table__number">{formatDuration(bucket.minutes)}</td>
                    <td className="table__number">{formatHours(bucket.minutes)}</td>
                    <td className="table__number">{formatDuration(bucket.billable_minutes)}</td>
                    <td className="table__number">{bucket.entries}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th scope="row">{t('time.total')}</th>
                  <td className="table__number">{formatDuration(report.data.total_minutes)}</td>
                  <td className="table__number">{formatHours(report.data.total_minutes)}</td>
                  <td className="table__number">
                    {formatDuration(report.data.total_billable_minutes)}
                  </td>
                  <td className="table__number">{report.data.total_entries}</td>
                </tr>
              </tfoot>
            </table>
          </div>
        ) : null}
      </section>
    </>
  );
}
